import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, LockKeyhole, Mail, UserRound, Users } from "lucide-react";
import api, { LOGO_URL_SYMBOL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatAuthError } from "@/lib/authErrors";

export default function TeamInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [info, setInfo] = useState(null);
    const [form, setForm] = useState({ first_name: "", last_name: "", password: "", confirm: "" });
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [accepted, setAccepted] = useState(false);

    useEffect(() => {
        api.get(`/crm/team/invite-info/${token}`)
            .then(({ data }) => {
                setInfo(data);
                setForm((current) => ({ ...current, first_name: data.first_name || "", last_name: data.last_name || "" }));
            })
            .catch((requestError) => setError(formatAuthError(requestError?.response?.data?.detail, "Invito non valido o già utilizzato")));
    }, [token]);

    const submit = async (event) => {
        event.preventDefault();
        setError("");
        if (info.account_mode !== "existing_password" && form.password !== form.confirm) {
            setError("Le password non coincidono");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/crm/team/complete-invite", {
                token,
                first_name: form.first_name,
                last_name: form.last_name,
                password: form.password,
            });
            setUser(data.user);
            setAccepted(true);
            setTimeout(() => navigate(data.redirect_to || "/analytics-pro", { replace: true }), 700);
        } catch (requestError) {
            setError(formatAuthError(requestError?.response?.data?.detail, "Impossibile completare l'invito"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main data-testid="team-invite-page" className="login-luxury-page">
            <div className="login-luxury-panel">
                <div className="login-luxury-logo" data-testid="teaminvite-logo-badge">
                    <img src={LOGO_URL_SYMBOL} alt="VendiDirect" draggable="false" />
                </div>
                {error && !info && <InviteError text={error} />}
                {!error && !info && <p data-testid="invite-loading" className="text-ink-400 text-center">Verifica invito in corso...</p>}
                {info && !accepted && (
                    <>
                        <div className="login-luxury-icon mx-auto"><Users size={24} /></div>
                        <h1 data-testid="invite-title" className="login-luxury-title text-center">Entra nel team</h1>
                        <p data-testid="invite-agency-name" className="login-luxury-subtitle text-center">Completa il tuo account per accedere al VD Manager di <strong className="text-gold">{info.agency_name}</strong>.</p>
                        <form data-testid="team-invite-setup-form" onSubmit={submit} className="login-credentials-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className="login-field"><span><UserRound size={14} /> Nome</span><input data-testid="invite-first-name" required minLength={1} maxLength={80} autoComplete="given-name" className="login-input" value={form.first_name} onChange={(event) => setForm({ ...form, first_name: event.target.value })} /></label>
                                <label className="login-field"><span><UserRound size={14} /> Cognome</span><input data-testid="invite-last-name" required minLength={1} maxLength={80} autoComplete="family-name" className="login-input" value={form.last_name} onChange={(event) => setForm({ ...form, last_name: event.target.value })} /></label>
                            </div>
                            <label className="login-field"><span><Mail size={14} /> Email</span><input data-testid="invite-email" readOnly className="login-input opacity-70" value={info.email || ""} /></label>
                            <label className="login-field"><span><LockKeyhole size={14} /> {info.account_mode === "existing_password" ? "Password attuale" : "Scegli una password"}</span><input data-testid="invite-password" required type="password" minLength={8} maxLength={128} autoComplete={info.account_mode === "existing_password" ? "current-password" : "new-password"} className="login-input" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder="Almeno 8 caratteri" /></label>
                            {info.account_mode !== "existing_password" && <label className="login-field"><span><LockKeyhole size={14} /> Conferma password</span><input data-testid="invite-password-confirm" required type="password" minLength={8} maxLength={128} autoComplete="new-password" className="login-input" value={form.confirm} onChange={(event) => setForm({ ...form, confirm: event.target.value })} placeholder="Ripeti la password" /></label>}
                            {info.account_mode === "existing_password" && <p data-testid="invite-existing-account-note" className="text-xs text-ink-400">Questa email possiede già un account: inserisci la password attuale per collegarlo all’agenzia.</p>}
                            {error && <InviteError text={error} compact />}
                            <button data-testid="invite-complete-button" type="submit" disabled={busy} className="login-submit-button">{busy ? <Loader2 size={17} className="animate-spin" /> : <Users size={17} />} {busy ? "Attivazione…" : info.account_mode === "existing_password" ? "Collega account e accedi" : "Crea account e accedi"}</button>
                        </form>
                    </>
                )}
                {accepted && <div data-testid="invite-accepted" className="text-center"><CheckCircle2 size={34} className="text-green-300 mx-auto mb-3" /><h2 className="font-serif text-2xl text-ink-50">Account attivato</h2><p className="text-ink-400 text-sm mt-2">Accesso effettuato. Ti stiamo portando al VD Manager…</p></div>}
            </div>
        </main>
    );
}

const InviteError = ({ text, compact = false }) => <div data-testid="invite-error" className={`login-alert login-alert-error ${compact ? "" : "text-center p-5"}`}><AlertTriangle size={compact ? 16 : 26} className={compact ? "inline mr-2" : "mx-auto mb-2"} />{text}</div>;